import { useEffect, useRef, useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Container, Grid, Typography, Card, CardContent, CircularProgress, Box } from '@mui/material';
import { listSensors, getReadings } from '../services/sensorTelemetryService';
import SensorSelectList from '../components/monitoring/SensorSelectList';
import MeasurementPicker from '../components/monitoring/MeasurementPicker';
import ReadingsChart from '../components/monitoring/ReadingsChart';
import { availableMeasurements, defaultMeasurement } from '../components/monitoring/measurementMeta';
import useAuth from '../hooks/useAuth';
import { prefersFahrenheit } from '../utils/temperature';

const WINDOW_MS = 3_600_000;
const POLL_MS = 45_000;

export default function SensorMonitoringPage() {
  const { user } = useAuth();
  const useFahrenheit = prefersFahrenheit(user);
  const [sensors, setSensors] = useState(null); // null = loading
  const [selectedId, setSelectedId] = useState(null);
  const [readings, setReadings] = useState([]);
  const [measurement, setMeasurement] = useState(null);
  const [loadingReadings, setLoadingReadings] = useState(false);
  const pollRef = useRef(null);

  useEffect(() => {
    let alive = true;
    listSensors().then((list) => { if (alive) setSensors(list || []); }).catch(() => { if (alive) setSensors([]); });
    return () => { alive = false; };
  }, []);

  const fetchReadings = useCallback((id) => {
    const now = Date.now();
    setLoadingReadings(true);
    return getReadings(id, now - WINDOW_MS, now)
      .then((res) => {
        const data = (res && res.readings) || [];
        setReadings(data);
        setMeasurement((prev) => {
          const fields = availableMeasurements(data);
          return prev && fields.includes(prev) ? prev : defaultMeasurement(fields);
        });
      })
      .catch(() => { /* keep last good readings on transient failure */ })
      .finally(() => setLoadingReadings(false));
  }, []);

  useEffect(() => {
    if (!selectedId) return undefined;
    fetchReadings(selectedId);
    pollRef.current = setInterval(() => fetchReadings(selectedId), POLL_MS);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedId, fetchReadings]);

  const onSelect = (id) => { setReadings([]); setMeasurement(null); setSelectedId(id); };

  const fields = availableMeasurements(readings);

  return (
    <>
      <Helmet><title>Monitoring | VPSi</title></Helmet>
      <Container maxWidth="xl">
        <Typography variant="h4" sx={{ mb: 3 }}>Monitoring</Typography>
        {sensors === null && <CircularProgress />}
        {sensors && sensors.length === 0 && (
          <Card><CardContent>
            <Typography>No sensors yet.</Typography>
            <SensorSelectList sensors={[]} selectedId={null} onSelect={() => {}} />
          </CardContent></Card>
        )}
        {sensors && sensors.length > 0 && (
          <Grid container spacing={3}>
            <Grid item xs={12} md={3}>
              <Card><SensorSelectList sensors={sensors} selectedId={selectedId} onSelect={onSelect} /></Card>
            </Grid>
            <Grid item xs={12} md={9}>
              <Card><CardContent>
                {!selectedId && <Typography>Select a sensor to view its readings.</Typography>}
                {selectedId && loadingReadings && readings.length === 0 && <CircularProgress />}
                {selectedId && !loadingReadings && readings.length === 0 && (
                  <Typography>No data yet — waiting for this sensor to report.</Typography>
                )}
                {selectedId && readings.length > 0 && measurement && (
                  <Box>
                    <MeasurementPicker fields={fields} selected={measurement} onSelect={setMeasurement} />
                    <ReadingsChart readings={readings} measurement={measurement} useFahrenheit={useFahrenheit} />
                  </Box>
                )}
              </CardContent></Card>
            </Grid>
          </Grid>
        )}
      </Container>
    </>
  );
}
